import type { Distributor, InventoryItem, Product } from "@/types";
import { formatCOP, formatNumber } from "@/utils/format";

export const itemUnits = (item?: InventoryItem): number =>
  item
    ? item.stockCasa +
      item.stockPuntoClear +
      Object.values(item.stockByAdvisor).reduce((s, n) => s + n, 0)
    : 0;

interface InventoryStatsProps {
  products: Product[];
  inventory: InventoryItem[];
  distributors: Distributor[];
}

const STAT_META = [
  { icon: "ri-archive-2-line", label: "Unidades en la red", bubble: "bg-primary-500/25 text-primary-100" },
  { icon: "ri-money-dollar-circle-line", label: "Valor a precio público", bubble: "bg-accent-500/20 text-accent-200" },
  { icon: "ri-stack-line", label: "Referencias con stock", bubble: "bg-secondary-500/25 text-secondary-200" },
  { icon: "ri-team-line", label: "Asesores con tenencia", bubble: "bg-accent-500/15 text-accent-100" },
];

// Resumen ejecutivo del inventario físico consolidado.
const InventoryStats = ({ products, inventory, distributors }: InventoryStatsProps) => {
  const totalUnits = inventory.reduce((acc, item) => acc + itemUnits(item), 0);
  const totalValue = products.reduce((acc, p) => {
    const item = inventory.find((i) => i.productId === p.id);
    return acc + itemUnits(item) * p.pricePublic;
  }, 0);
  const withStock = inventory.filter((i) => itemUnits(i) > 0).length;
  const advisorsWithStock = distributors.filter((d) =>
    inventory.some((i) => (i.stockByAdvisor[d.id] ?? 0) > 0)
  ).length;

  const values = [
    formatNumber(totalUnits),
    formatCOP(totalValue),
    formatNumber(withStock),
    formatNumber(advisorsWithStock),
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">
      {STAT_META.map((meta, i) => (
        <div key={meta.label} className="glass-panel rounded-lg p-4 animate-fade-up">
          <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${meta.bubble}`}>
            <i className={`${meta.icon} text-lg`} />
          </span>
          <p className="text-[11px] text-foreground-500 mt-3">{meta.label}</p>
          <p className="text-lg md:text-xl font-heading font-extrabold text-foreground-950 mt-0.5 break-words">
            {values[i]}
          </p>
        </div>
      ))}
    </div>
  );
};

export default InventoryStats;